import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApplicationConfigService } from '../../core/config/application-config.service';
import { LocalStorageService } from 'ngx-webstorage';

@Injectable({
  providedIn: 'root'
})
export class LoginOtpService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private applicationConfigService: ApplicationConfigService
  ) {
    // try to follow app patterns: prefer getEndpointFor('api') if available
    this.baseUrl = this.applicationConfigService.getEndpointFor
      ? this.applicationConfigService.getEndpointFor('api')
      : (this.applicationConfigService.getEndpointFor ? this.applicationConfigService.getEndpointFor('api') : '/api');
  }

  /** Send OTP using an email or mobile identifier (isForgetPassword = false). */
  sendOtp(email: string): Observable<string> {
    const trimmed = (email || '').trim();
    if (!trimmed) {
      return throwError(() => new Error('Please provide a valid email address or mobile number.'));
    }

    return this.http.post(
      `${this.baseUrl}/login/send-otp`,
      { email: trimmed },
      { headers: this.jsonHeaders(), responseType: 'text' }
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /** Verify OTP for login */
  verifyOtp(email: string, otp: string): Observable<any> {
    const params = new HttpParams().set('isForgetPassword', 'false');

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const tenantId = this.localStorageService.retrieve('tenantId');
    if (tenantId) headers = headers.set('X-Tenant-ID', tenantId);

    return this.http.post<any>(
      `${this.baseUrl}/login/verify-otp`,
      { email: (email || '').trim(), otp: (otp || '').trim() },
      { headers, params }
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /** Save token + user info after OTP login */
  loginWithOtpSuccess(token: string, email: string, roles?: string[]) {
    this.localStorageService.store('authenticationToken', token);
    this.localStorageService.store('email', email);
    if (roles) this.localStorageService.store('USER_ROLE', roles);
  }

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  /**
   * Central error handler — returns a friendly Error with message property.
   * Map known backend exception messages to user friendly text.
   */
  private handleError(error: any): Observable<never> {
    let message = 'Server error occurred. Please try again.';

    if (typeof error?.error === 'string' && error.error.trim()) {
      try {
        const parsed = JSON.parse(error.error);
        message = parsed.message || parsed.error || error.error;
      } catch (e) {
        message = error.error;
      }
    } else if (error?.error?.message) {
      message = error.error.message;
    } else if (error?.error?.error) {
      message = error.error.error;
    } else if (error?.statusText && error?.statusText !== 'OK') {
      message = error.statusText;
    } else if (error?.message) {
      message = error.message;
    }

    console.error('LoginOtpService Error:', message, error);

    if (message.includes('Index 0 out of bounds') || message.toLowerCase().includes('indexoutofbound')) {
      message = 'The entered email address or mobile number is not valid.';
    }

    return throwError(() => new Error(message));
  }
}
